import { Box, Grid } from "@mui/material";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { message, responseErrors, responseDownloadFileErrors, responseSuccess } from "utils";
import { HeaderContentBar } from "components/Layout";
import { AlertMessage } from "components/UI";
import ActionBar from "../../Partials/ActionBar";
import { ModeAction } from "state/enum";
import { useConfirmDialogContext } from "context/confirmDialogContext";
import { ModalContainerGroup } from "pages/Master/ContainerGroupScreen/component/ContainerDetail/ModalContainerGroup";
import useColumn from "./hooks/useColumn";

import {
  useContainerGroupGetApmcQuery,
  useContainerGroupGetContainerTypeQuery,
  useContainerGroupDownloadExcelMutation,
  useContainerGroupSearchMutation,
  useContainerGroupDeleteMutation,
} from "shared/services/master";
import { COMMON_ENUM, FIRST_PAGE } from "shared/constants";
import { useSelector } from "react-redux";
import ContainerTable from "./component/ContainerTable";
import ContainerCriterial from "./component/ContainerCriterial";

const ContainerGroupScreen = () => {
  const userInfo = useSelector(state => state.auth.user);
  const confirmDialogCtx = useConfirmDialogContext();

  const [mode, setMode] = useState("view");
  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  const [onSearch, setOnSearch] = useState(false);
  const [msgError, setMsgError] = useState([]);
  const [msgAlert, setMsgAlert] = useState([]);

  useEffect(() => {
    document.getElementById("select_apmc").focus();
  }, []);

  const [searchForm, setSearchForm] = useState({
    refCompanyId: "",
    refPlantId: "",
    containerType: "",
    containerGroupCode: "",
  });
  // 1. pagination
  const [pagination, setPagination] = useState({});
  const [pageNumber, setPageNumber] = useState(1);

  // 2. api
  const { data: apmcData } = useContainerGroupGetApmcQuery();

  const { data: containerTypeData } = useContainerGroupGetContainerTypeQuery();
  const downloadExcel = useContainerGroupDownloadExcelMutation();
  const searchDataAsync = useContainerGroupSearchMutation();
  const deleteContainerGroup = useContainerGroupDeleteMutation();
  // handle click detail
  const [rowNumber, setRowNumber] = useState();
  const [modalForm, setModalForm] = useState({
    apmc: "",
    containerGroupCode: "",
    containerTypeId: "",
    numRetry: "",
    score: "",
    effDtFrom: "",
    effDtTo: "",
    rPackageType: [],
    warehouse: [],
  });
  const handleClickDetail = row => {
    setModalForm({
      apmc: row?.refImpExpCd,
      containerGroupCode: row?.containerGroupCd,
      containerTypeId: row?.containerTypeId,
      numRetry: row?.numRetry,
      score: row?.priority,
      effDtFrom: row?.effDtFrom,
      effDtTo: row?.effDtTo,
      rPackageType: row?.rPkgType,
      warehouse: row?.warehouse,
    });
    setRowNumber(row.id);
    setMode(ModeAction.VIEW);
    setIsOpenModalContainerGroup(true);
  };

  // 4. columns
  const columns = useMemo(() => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useColumn({ rows, mode, handleClickDetail });
  }, [rows, mode]);

  // 6. handleSearch
  const handleSearch = async e => {
    try {
      e.preventDefault();
      setMsgError([]);
      setMsgAlert([]);
      setMode(ModeAction.VIEW);
      setRows([]);
      setRowSelectionModel([]);
      setRowModesModel({});
      setOnSearch(true);

      const searchData = await searchDataAsync({
        ...searchForm,
        dataOwner: COMMON_ENUM.C_SM_CTG_TMAP_EM,
        pageNumber: FIRST_PAGE,
      });

      const data =
        searchData?.result?.data?.map((item, index) => ({
          rowNumber: index + 1,
          ...item,
        })) ?? [];

      // data not found
      if (!data.length) {
        setMsgError([message({ type: "notFound" })]);
      }

      setPagination(searchData?.result?.pagination ?? {});
      setPageNumber(searchData?.result?.pagination?.pageNumber);
      setRows(data);
    } catch (error) {
      const errors = responseErrors(error);
      setMsgError(errors);
      return;
    }
  };

  // 7. handleDeleteClick
  const handleDeleteClick = async () => {
    setMsgError([]);
    setMsgAlert([]);
    const confirm = await confirmDialogCtx.success({ type: "confirmDelete" });
    if (!confirm) {
      return;
    }
    // TODO: Action Delete
    try {
      const selectedNo = rowSelectionModel[0];
      const row = rows.find(v => v.rowNumber === selectedNo);
      const body = { updateBy: userInfo.userName, updateDt: row.updateDt };
      const response = await deleteContainerGroup(row.containerGroupId, body);
      const msg = responseSuccess(response);
      setRowSelectionModel([]);
      setMode("view");
      setMsgAlert([msg]);
      window.scroll(0, 0);
      await refetchSearch();

      return;
    } catch (error) {
      const errors = responseErrors(error);
      setMsgError(errors);
      window.scroll(0, 0);
      return;
    }
  };

  // 9. handleClear
  const handleClear = () => {
    setMode(ModeAction.VIEW);
    setMsgError([]);
    setMsgAlert([]);

    setSearchForm({
      dataOwner: COMMON_ENUM.C_SM_CTG_TMAP_EM, // TODO: get dataOwner from profile
      refCompanyId: "",
      refPlantId: "",
      containerType: "",
      containerGroupCode: "",
      userId: userInfo.userName,
    });
    setModalForm({
      apmc: "",
      containerGroupCode: "",
      containerTypeId: "",
      numRetry: "",
      score: "",
      effDtFrom: "",
      effDtTo: "",
      rPackageType: [],
      warehouse: [],
      defaultData: {},
    });
    setRowModesModel({});
    setRowSelectionModel([]);
    setOnSearch(false);
    setRows([]);
    document.getElementById("select_apmc").focus();
  };

  // 10. handleDownloadExcel
  const handleDownloadExcel = async () => {
    // validate
    setMsgError([]);
    setMsgAlert([]);
    try {
      const searchData = await searchDataAsync({
        ...searchForm,
        pageNumber: 1,
      });

      // data not found
      if (!searchData?.result?.data.length) {
        setMsgError([message({ type: "notFound" })]);
      }
    } catch (error) {
      const errors = responseErrors(error);
      setMsgError(errors);
      window.scrollTo(0, 0);
      return;
    }
    try {
      const body = {
        refCompanyId: searchForm.refCompanyId,
        refPlantId: searchForm.refPlantId,
        containerType: searchForm.containerType,
        containerGroupCode: searchForm.containerGroupCode,
        pageNumber: pageNumber,
      };
      await downloadExcel(body);
    } catch (error) {
      const errors = responseDownloadFileErrors(error);
      setMsgError(errors);
      return;
    }
  };

  // modal container group
  const [isOpenModalContainerGroup, setIsOpenModalContainerGroup] = useState(false);

  const handleAdd = async () => {
    setModalForm({
      apmc: "",
      containerGroupCode: "",
      containerTypeId: "",
      numRetry: "",
      score: "",
      effDtFrom: "",
      effDtTo: "",
      rPackageType: [],
      warehouse: [],
    });
    setMsgError([]);
    setMsgAlert([]);

    setMode(ModeAction.ADD);
    setRowNumber(0);
    setIsOpenModalContainerGroup(true);
  };

  // handle edit
  const handleEdit = async () => {
    setMsgError([]);
    setMsgAlert([]);
    const row = rows.find(val => val.rowNumber === rowSelectionModel[0]);
    setModalForm({
      // ...row,
      apmc: row?.refImpExpCd,
      containerGroupCode: row?.containerGroupCd,
      containerTypeId: row?.containerTypeId,
      numRetry: row?.numRetry,
      score: row?.priority,
      effDtFrom: row?.effDtFrom,
      effDtTo: row?.effDtTo,
      rPackageType: row?.rPkgType,
      warehouse: row?.warehouse,
      defaultData: row,
    });
    setMode(ModeAction.EDIT);
    setRowNumber(rowSelectionModel[0]);
    setIsOpenModalContainerGroup(true);
    setRowSelectionModel([]);
  };

  // handle copy
  const handleCopy = () => {
    setMsgError([]);
    setMsgAlert([]);
    const row = rows.find(val => val.rowNumber === rowSelectionModel[0]);
    setModalForm({
      apmc: row?.refImpExpCd,
      containerGroupCode: "",
      containerTypeId: row?.containerTypeId,
      numRetry: row?.numRetry,
      score: row?.priority,
      effDtFrom: "",
      effDtTo: "",
      rPackageType: row?.rPkgType,
      warehouse: row?.warehouse,
    });
    setMode(ModeAction.ADD);
    setRowNumber(rowSelectionModel[0]);
    setIsOpenModalContainerGroup(true);
  };

  const refetchSearch = async () => {
    try {
      // setRows([]);
      const searchData = await searchDataAsync({
        ...searchForm,
        pageNumber: pageNumber,
      });
      const data =
        searchData?.result?.data?.map((item, index) => ({
          rowNumber: index + 1,
          ...item,
        })) ?? [];

      // data not found
      if (!data.length) {
        setOnSearch(false);
        setPagination({});
        setPageNumber(FIRST_PAGE);
        setRows([]);
        setRowModesModel({});
        setRowSelectionModel([]);
        return;
      }

      setPagination(searchData?.result?.pagination ?? {});
      setPageNumber(searchData?.result?.pagination?.pageNumber);
      setRows(data);
      setOnSearch(true);
    } catch (error) {
      const errors = responseErrors(error);
      setMsgError(errors);
      return;
    }
  };
  return (
    <Fragment>
      <Box sx={{ padding: "1rem" }}>
        {/* Header */}
        <HeaderContentBar title="WDN91090 : Container Group Master Maintenance Screen" />
        {/* Alert Message */}
        <AlertMessage type={"warning"} message={msgError} />
        <AlertMessage type={"success"} message={msgAlert} />
        <Grid container spacing={0} sx={{ alignItems: "center" }}>
          <ContainerCriterial
            mode={mode}
            searchForm={searchForm}
            setSearchForm={setSearchForm}
            apmcData={apmcData}
            containerTypeData={containerTypeData}
          />
        </Grid>
      </Box>
      <ActionBar
        mode={mode}
        rows={rows}
        columns={columns}
        setRows={setRows}
        setMode={setMode}
        setMsgError={setMsgError}
        setMsgAlert={setMsgAlert}
        setOnSearch={setOnSearch}
        setRowModesModel={setRowModesModel}
        setRowSelectionModel={setRowSelectionModel}
        rowSelectionModel={rowSelectionModel}
        rowModesModel={rowModesModel}
        handleSearch={handleSearch}
        handleClear={handleClear}
        handleDownloadExcel={handleDownloadExcel}
        handleDeleteClick={handleDeleteClick}
        handleAdd={handleAdd}
        handleEdit={handleEdit}
        handleCopy={handleCopy}
        firstField={"select_apmc"}
        functionId={"WDN91090"}
      />
      <Box sx={{ padding: "1rem" }}>
        <ContainerTable
          searchForm={searchForm}
          mode={mode}
          onSearch={onSearch}
          rowSelectionModel={rowSelectionModel}
          setRowSelectionModel={setRowSelectionModel}
          setMode={setMode}
          rows={rows}
          rowModesModel={rowModesModel}
          columns={columns}
          setRowModesModel={setRowModesModel}
          pagination={pagination}
          setPagination={setPagination}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          setRows={setRows}
          setMsgError={setMsgError}
          functionId={"WDN91090"}
        />
      </Box>

      {/* ModalContainerGroup */}
      <ModalContainerGroup
        modalForm={modalForm}
        setModalForm={setModalForm}
        open={isOpenModalContainerGroup}
        setOpen={setIsOpenModalContainerGroup}
        initialData={[]}
        mode={mode}
        setMode={setMode}
        rows={rows}
        rowNumber={rowNumber}
        setRowModesModel={setRowModesModel}
        setRowSelectionModel={setRowSelectionModel}
        refetchSearch={refetchSearch}
        setMsgAlertMainScreen={setMsgAlert}
        apmcData={apmcData}
      />
    </Fragment>
  );
};

export default ContainerGroupScreen;
